import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, BookOpen, X } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NotificationBell = () => {
  const { isAuthenticated, token } = useAuth();
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [newStories, setNewStories] = useState([]);
  const [showPanel, setShowPanel] = useState(false);

  useEffect(() => {
    if (isAuthenticated) fetchNewStories();
  }, [isAuthenticated]);

  const fetchNewStories = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const res = await axios.get(`${API}/notifications/new-stories`, { headers });
      setNewStories(res.data.new_stories || []);
    } catch (e) { /* Ignore */ }
  };

  const markSeen = async () => {
    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(`${API}/notifications/mark-seen`, {}, { headers });
      setNewStories([]);
      setShowPanel(false);
    } catch (e) { /* Ignore */ }
  };

  if (!isAuthenticated) return null;

  return (
    <div className="relative">
      <Button data-testid="notification-bell" variant="ghost" size="sm" onClick={() => setShowPanel(!showPanel)} className="rounded-full px-3 relative">
        <Bell className="w-4 h-4" />
        {newStories.length > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center font-bold">
            {newStories.length > 9 ? '9+' : newStories.length}
          </span>
        )}
      </Button>

      {showPanel && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowPanel(false)} />
          <div className="absolute right-0 top-12 z-50 bg-card border border-border rounded-2xl shadow-xl w-72 max-h-80 overflow-hidden" data-testid="notification-panel">
            <div className="flex items-center justify-between p-3 border-b border-border">
              <span className="font-bold text-sm text-foreground">{t('notif.new_stories')}</span>
              <button onClick={() => setShowPanel(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
            </div>
            <div className="overflow-y-auto max-h-56">
              {newStories.length === 0 ? (
                <div className="p-4 text-center text-sm text-muted-foreground">{t('notif.no_new')}</div>
              ) : (
                newStories.map((story) => (
                  <button key={story.id} onClick={() => { navigate(`/story/${story.id}`); setShowPanel(false); }} className="w-full flex items-center gap-3 p-3 hover:bg-muted/50 transition-colors text-left" data-testid={`notif-story-${story.id}`}>
                    {story.cover_image_url ? (
                      <div className="w-10 h-12 rounded bg-cover bg-center flex-shrink-0" style={{ backgroundImage: `url(${story.cover_image_url})` }} />
                    ) : (
                      <div className="w-10 h-12 rounded bg-gold/20 flex items-center justify-center flex-shrink-0"><BookOpen className="w-4 h-4 text-gold" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm text-foreground truncate">{story.title}</p>
                      <p className="text-xs text-muted-foreground">{t('misc.by')} {story.grandma_name}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
            {newStories.length > 0 && (
              <div className="p-2 border-t border-border">
                <Button data-testid="mark-seen-btn" variant="ghost" size="sm" onClick={markSeen} className="w-full text-xs">{t('notif.mark_seen')}</Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
